import React, { type ComponentProps, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { StyleSheet, View, type ViewStyle } from 'react-native';
import {
  BodyText,
  DisplayText,
  Scrollspy,
  theme,
  useContainerSpacing,
  useResponsive,
} from '@sg/garnish';

import { IngredientModificationsTypes } from '@order/utils';

import { messages as globalMessages } from '../../CustomizationIngredientsGrid.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationIngredientsGridSection = (
  props: CustomizationIngredientsGridSectionProps,
) => {
  const { ingredientKindName, formattedQuantity, children, ...restProps } =
    props;

  const { match } = useResponsive();
  const containerSpacing = useContainerSpacing();

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useMemo<ViewStyle>(() => {
    return { paddingHorizontal: containerSpacing };
  }, [containerSpacing]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Scrollspy.TargetView
      style={[
        match([styles.containerXS, styles.containerSM]),
        containerDynamicStyles,
      ]}
      targetId={ingredientKindName}
      {...restProps}
    >
      <View style={styles.label}>
        <DisplayText sizeMatch={['24']} bold>
          <FormattedMessage
            {...globalMessages.ingredientKind}
            values={{ ingredient_kind: ingredientKindName }}
          />
        </DisplayText>

        {formattedQuantity ? (
          <BodyText sizeMatch={['16']}>{formattedQuantity}</BodyText>
        ) : null}
      </View>

      {ingredientKindName === IngredientModificationsTypes.Dressings ? (
        <BodyText>
          <FormattedMessage {...globalMessages.dressingsHelperText} />
        </BodyText>
      ) : null}

      <View style={styles.contentContainer}>{children}</View>
    </Scrollspy.TargetView>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    paddingTop: theme.spacing['6'],
  },
  containerSM: {
    paddingTop: theme.spacing['10'],
  },
  contentContainer: {
    marginTop: theme.spacing['4'],
  },
  label: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationIngredientsGridSectionProps = {
  ingredientKindName: string;
  formattedQuantity?: string;
} & Omit<ComponentProps<typeof Scrollspy.TargetView>, 'targetId'>;
